import { useState } from 'react';
import Editor from '@monaco-editor/react';
import {
    StyledModal,
    ModalTitle,
    ToolCallList,
    StyledAccordion,
    StyledAccordionSection,
    ToolCallDetails,
    DetailSection,
    SectionLabel,
    MonacoContainer,
    ErrorBlock,
    EmptyResult,
} from './styled';

export interface ResolvedToolCall {
    id: string;
    name: string;
    arguments: string;
    result?: string;
    error?: string;
}

interface ToolCallsModalProps {
    isOpen: boolean;
    onRequestClose: () => void;
    toolCalls: ResolvedToolCall[];
}

const formatJson = (value: string): string => {
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
};

const getEditorHeight = (value: string) => {
    const lineCount = value.split('\n').length;
    const lineHeight = 19;
    const padding = 16;
    return Math.min(lineCount * lineHeight + padding, 320);
};

const MONACO_OPTIONS = {
    readOnly: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
    lineNumbers: 'off' as const,
    folding: true,
    renderLineHighlight: 'none' as const,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    scrollbar: { vertical: 'hidden' as const, horizontal: 'hidden' as const },
    padding: { top: 8, bottom: 8 },
};

interface JsonViewerProps {
    value: string;
}

const JsonViewer = ({ value }: JsonViewerProps) => {
    const formatted = formatJson(value);
    const height = getEditorHeight(formatted);
    return (
        <MonacoContainer $height={height}>
            <Editor
                value={formatted}
                language="json"
                height={height}
                options={MONACO_OPTIONS}
                theme="vs"
            />
        </MonacoContainer>
    );
};

interface ToolCallItemProps {
    tc: ResolvedToolCall;
}

const ToolCallItem = ({ tc }: ToolCallItemProps) => {
    const [activeSection, setActiveSection] = useState<string | undefined>();

    return (
        <StyledAccordion
            activeSectionNames={activeSection}
            onToggleSection={(_, name) => setActiveSection(
                activeSection === name ? undefined : name as string,
            )}
        >
            <StyledAccordionSection name={tc.id} label={tc.name}>
                <ToolCallDetails>
                    <DetailSection>
                        <SectionLabel>Arguments</SectionLabel>
                        <JsonViewer value={tc.arguments} />
                    </DetailSection>
                    {tc.error ? (
                        <DetailSection>
                            <SectionLabel>Error</SectionLabel>
                            <ErrorBlock>{tc.error}</ErrorBlock>
                        </DetailSection>
                    ) : (
                        <DetailSection>
                            <SectionLabel>Result</SectionLabel>
                            {tc.result !== undefined ? (
                                <JsonViewer value={tc.result} />
                            ) : (
                                <EmptyResult>—</EmptyResult>
                            )}
                        </DetailSection>
                    )}
                </ToolCallDetails>
            </StyledAccordionSection>
        </StyledAccordion>
    );
};

const ToolCallsModal = ({ isOpen, onRequestClose, toolCalls }: ToolCallsModalProps) => (
    <StyledModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        borderRadius="semi-square"
        title={<ModalTitle>Tool Calls</ModalTitle>}
    >
        <ToolCallList>
            {toolCalls.map((tc) => (
                <ToolCallItem key={tc.id} tc={tc} />
            ))}
        </ToolCallList>
    </StyledModal>
);

export default ToolCallsModal;
